import React, { useState, useEffect } from 'react';
import './Navbar.css';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [theme, setTheme] = useState(() => {
        return localStorage.getItem('theme') || 'light';
    });
    const [activeSection, setActiveSection] = useState('home');

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    useEffect(() => {
        const handleScroll = () => {
            const sections = ['home', 'categories', 'contact'];
            const scrollPosition = window.scrollY + 100;

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const offsetTop = element.offsetTop;
                    const offsetBottom = offsetTop + element.offsetHeight;

                    if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        setIsSearchOpen(false); // Close search when menu opens
        document.body.style.overflow = !isMenuOpen ? 'hidden' : '';
    };

    const toggleSearch = () => {
        setIsSearchOpen(!isSearchOpen);
    };

    const toggleTheme = () => {
        setTheme(theme === 'light' ? 'dark' : 'light');
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        document.body.style.overflow = '';
    };

    const handleNavClick = (e, sectionId) => {
        e.preventDefault();
        closeMenu();
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            // Search logic here
            performSearch(searchQuery);
        }
    };

    const performSearch = (query) => {
        // Normalize search query
        const normalizedQuery = query.toLowerCase().trim();
        
        // Define searchable categories and their keywords
        const categoryKeywords = {
            'professional-services': ['professional', 'service', 'consulting', 'logistics', 'procurement', 'supply chain', 'business'],
            'healthcare-pharmaceutical': ['health', 'healthcare', 'medicine', 'medical', 'pharmacy', 'pharmaceutical', 'drug', 'supplement', 'vitamin'],
            'ecommerce': ['ecommerce', 'e-commerce', 'online', 'shop', 'digital', 'retail', 'delivery', 'fulfilment'],
            'hospitality-essentials': ['hospitality', 'hotel', 'towel', 'linen', 'bathrobe', 'amenity', 'amenities', 'guest'],
            'food-provisions': ['food', 'provision', 'pulse', 'grain', 'lentil', 'rice', 'wheat', 'chickpea', 'bean', 'quinoa'],
            'cleaning-hygiene': ['clean', 'cleaning', 'hygiene', 'sanitation', 'disinfectant', 'sanitiser', 'hygenic', 'wash'],
            'tableware-kitchenware': ['tableware', 'kitchenware', 'plate', 'cutlery', 'glass', 'utensil', 'cookware', 'dining', 'kitchen'],
            'furniture': ['furniture', 'sofa', 'chair', 'table', 'bed', 'desk', 'cabinet', 'storage', 'furnishing']
        };

        // Find matching category
        let matchedCategory = null;
        for (const [categoryId, keywords] of Object.entries(categoryKeywords)) {
            if (keywords.some(keyword => normalizedQuery.includes(keyword))) {
                matchedCategory = categoryId;
                break;
            }
        }

        if (matchedCategory) {
            // Scroll to categories section
            const categoriesSection = document.getElementById('categories');
            if (categoriesSection) {
                categoriesSection.scrollIntoView({ behavior: 'smooth' });
                
                // After scrolling, trigger the category card click
                setTimeout(() => {
                    const categoryCard = document.querySelector(`[data-category="${matchedCategory}"]`);
                    if (categoryCard) {
                        categoryCard.click();
                    }
                }, 500);
            }
        } else {
            // If no match, just scroll to categories
            const categoriesSection = document.getElementById('categories');
            if (categoriesSection) {
                categoriesSection.scrollIntoView({ behavior: 'smooth' });
            }
            alert(`No exact match found for "${query}". Showing all categories.`);
        }

        // Clear search
        setSearchQuery('');
        setIsSearchOpen(false);
    };

    // Logo path based on theme
    const logoSrc = theme === 'dark' 
        ? '/assets/aroha-dark.jpeg' 
        : '/assets/aroha-light.jpeg';

    return (
        <>
            <nav className="nav">
                <div className="nav-container">
                    <a href="#home" onClick={(e) => handleNavClick(e, 'home')} aria-label="Aroha Home">
                        <img src={logoSrc} alt="Aroha" className="nav-logo" />
                    </a>

                    <ul className="nav-menu">
                        <li>
                            <a 
                                href="#home" 
                                className={`nav-link ${activeSection === 'home' ? 'active' : ''}`}
                                onClick={(e) => handleNavClick(e, 'home')}
                            >
                                Home
                            </a>
                        </li>
                        <li>
                            <a 
                                href="#categories" 
                                className={`nav-link ${activeSection === 'categories' ? 'active' : ''}`}
                                onClick={(e) => handleNavClick(e, 'categories')}
                            >
                                Categories
                            </a>
                        </li>
                        <li>
                            <a 
                                href="#contact" 
                                className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
                                onClick={(e) => handleNavClick(e, 'contact')}
                            >
                                Contact Us
                            </a>
                        </li>
                    </ul>

                    {/* Desktop Search */}
                    <div className="nav-search">
                        <form onSubmit={handleSearch}>
                            <svg className="nav-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input 
                                type="search" 
                                className="nav-search-input" 
                                placeholder="Search products..." 
                                aria-label="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </form>
                    </div>

                    <div className="nav-contact">
                        <a href="tel:+1234567890" className="nav-contact-item">
                            <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            <span>+971 58 953 2066</span>
                        </a>
                        <a href="mailto:hello@arohaint.com" className="nav-contact-item">
                            <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <span>hello@arohaint.com</span>
                        </a>
                    </div>

                    <button className="theme-toggle-desktop" onClick={toggleTheme} aria-label="Toggle theme">
                        {theme === 'light' ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="5"/>
                                <line x1="12" y1="1" x2="12" y2="3"/>
                                <line x1="12" y1="21" x2="12" y2="23"/>
                                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                                <line x1="1" y1="12" x2="3" y2="12"/>
                                <line x1="21" y1="12" x2="23" y2="12"/>
                                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                            </svg>
                        )}
                    </button>

                    {/* Mobile/Tablet Right Section */}
                    <div className="nav-right">
                        {/* Mobile Search Icon */}
                        <button className="search-icon-btn" onClick={toggleSearch} aria-label="Search">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                        </button>

                        <button className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu} aria-label="Menu">
                            <span className="hamburger-line"></span>
                            <span className="hamburger-line"></span>
                            <span className="hamburger-line"></span>
                        </button>
                    </div>
                </div>

                {/* Mobile Search Bar */}
                {isSearchOpen && (
                    <div className="mobile-search-bar">
                        <form onSubmit={handleSearch}>
                            <input 
                                type="search" 
                                className="mobile-search-input" 
                                placeholder="Search products..." 
                                aria-label="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                            />
                            <button type="submit" className="mobile-search-submit">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"/>
                                    <path d="m21 21-4.35-4.35"/>
                                </svg>
                            </button>
                        </form>
                    </div>
                )}
            </nav>

            {/* Mobile Menu */}
            <div className={`mobile-menu ${isMenuOpen ? 'active' : ''}`}>
                <ul className="mobile-nav-links">
                    <li>
                        <a href="#home" className="mobile-nav-link" onClick={(e) => handleNavClick(e, 'home')}>
                            Home
                        </a>
                    </li>
                    <li>
                        <a href="#categories" className="mobile-nav-link" onClick={(e) => handleNavClick(e, 'categories')}>
                            Categories
                        </a>
                    </li>
                    <li>
                        <a href="#contact" className="mobile-nav-link" onClick={(e) => handleNavClick(e, 'contact')}>
                            Contact Us
                        </a>
                    </li>
                </ul>

                <div className="mobile-contact">
                    <a href="tel:+1234567890" className="nav-contact-item">
                        <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        <span>+971 58 953 2066</span>
                    </a>
                    <a href="mailto:hello@arohaint.com" className="nav-contact-item">
                        <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        <span>hello@arohaint.com</span>
                    </a>
                </div>

                <div className="mobile-theme-toggle">
                    <button className="theme-toggle-btn" onClick={toggleTheme}>
                        {theme === 'light' ? (
                            <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                                </svg>
                                <span>Dark Mode</span>
                            </>
                        ) : (
                            <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="5"/>
                                    <line x1="12" y1="1" x2="12" y2="3"/>
                                    <line x1="12" y1="21" x2="12" y2="23"/>
                                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                                    <line x1="1" y1="12" x2="3" y2="12"/>
                                    <line x1="21" y1="12" x2="23" y2="12"/>
                                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                                </svg>
                                <span>Light Mode</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default Navbar;